<template>
  <div class="interview-wrapper">
    <!-- Loading Spinner -->
    <div v-if="loadingUnity" id="spinner" class="spinner">
      <div class="spinner-animation">
        <v-row class="center-spinner mb-8">
          <v-progress-circular :size="80" indeterminate class="primary-color" />
        </v-row>
        <v-row>
          <p class="primary-color">La entrevista se está configurando...</p>
        </v-row>
      </div>
    </div>

    <!-- Interview Title - Above avatar -->
    <div class="interview-title-bar">
      <h2 v-if="interview.position !== undefined" class="primary-color interview-title">
        {{ interview.position.name || '' }}
      </h2>
    </div>

    <!-- Main Avatar Container - Full Width -->
    <div class="avatar-main-container">
        <!-- Progress Bar - Top overlay -->
        <div class="progress-overlay">
          <v-progress-linear
            :value="progressPercentage"
            height="22"
            color="#505192"
            class="interview-progress-bar"
          >
            <template #default>
              <strong class="white--text">{{ progressPercentage }}%</strong>
            </template>
          </v-progress-linear>
        </div>

        <!-- Avatar Dialog - Top left overlay -->
        <v-card
          id="callPlayerTextAvatar"
          class="avatar-dialog-overlay overflow-y-auto pa-3"
        >
          <p class="avatar-dialog-text mb-0">
            {{ avatarMessage }}
          </p>
        </v-card>

        <!-- Unity Canvas Container -->
        <div id="unity-container" class="unity-container-fullwidth">
          <canvas
            id="unity-canvas"
            class="unity-canvas-fullwidth"
            tabindex="-1"
          ></canvas>
          <v-row
            id="unity-loading-bar"
            justify="center"
            class="mt-4"
            style="display: none !important"
          >
            <v-col cols="12" md="8">
              <div id="unity-logo"></div>
              <div id="unity-progress-bar-empty">
                <div id="unity-progress-bar-full"></div>
              </div>
            </v-col>
          </v-row>
          <v-row style="display: none !important">
            <v-col cols="12" md="8">
              <div id="unity-warning"></div>
            </v-col>
          </v-row>
        </div>

        <!-- Candidate Video - Bottom left overlay (white card) -->
        <v-card class="candidate-video-card elevation-4">
          <video
            id="candidateVideo"
            ref="candidateVideo"
            class="candidate-video"
            poster="img/no_camera_found_girado.png"
            autoplay
            muted
          ></video>
        </v-card>

        <!-- Controls Card - Bottom right overlay -->
        <v-card class="controls-card elevation-6">
          <div class="controls-card-content">
            <v-stepper v-if="dynamicSteps.length" v-model="actualStep" class="controls-stepper">
              <v-stepper-items>
                <v-stepper-content
                  v-for="(item, index) in dynamicSteps"
                  :key="index"
                  :step="item.order + 1"
                  class="pa-2"
                >
                  <!-- INTRODUCTION -->
                  <div v-if="item.type == 'INTRODUCTION'" class="step-content">
                  </div>

                  <!-- VIDEO -->
                  <div v-if="item.type == 'VIDEO' && stepsInterview && !finalStep" class="step-content">
                    <div v-if="video !== ''" class="video-container">
                      <iframe
                        class="step-video"
                        :src="video"
                        title="Video Player"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowfullscreen
                      ></iframe>
                    </div>
                    <v-btn
                      v-if="actualTypeStep !== 'TEST' && actualTypeStep !== 'QUESTION' && actualTypeStep !== 'INTRODUCTION' && !finalStep"
                      color="primary"
                      block
                      class="mt-3"
                      :disabled="!lastAudio"
                      @click="nextStep()"
                    >
                      Continue
                    </v-btn>
                  </div>

                  <!-- IFRAME -->
                  <div v-if="item.type == 'IFRAME' && stepsInterview && !finalStep" class="step-content">
                    <div v-if="video !== ''" class="video-container">
                      <iframe
                        class="step-video"
                        :src="video"
                        title="Video Player"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowfullscreen
                      ></iframe>
                    </div>
                    <v-btn
                      v-if="actualTypeStep === 'IFRAME' && !finalStep"
                      color="primary"
                      block
                      class="mt-3"
                      :disabled="!lastAudio || !isEndUrl"
                      @click="nextStep()"
                    >
                      Continue
                    </v-btn>
                  </div>

                  <!-- TEST -->
                  <div v-if="item.type == 'TEST' && stepsInterview && !finalStep" class="step-content">
                    <!-- Recording Controls -->
                    <div class="recording-controls-compact">
                      <v-btn
                        v-if="!isTranscripted"
                        :disabled="loadingAnswer || !lastAudio"
                        class="ok-modal recording-btn"
                        :class="{ recording: isRecordingQuestion }"
                        block
                        @click="toggleRecording"
                      >
                        <v-icon v-if="isRecordingQuestion" class="mr-2">mdi-record-circle-outline</v-icon>
                        <v-icon v-else class="mr-2">mdi-record-rec</v-icon>
                        {{ isRecordingQuestion ? 'Recording...' : 'Start recording' }}
                      </v-btn>
                      <v-btn
                        v-else
                        :disabled="isSendingQuestion || !lastAudio || answerTest.toString().trim() === ''"
                        class="ok-modal recording-btn"
                        block
                        @click="toggleRecording"
                      >
                        <v-icon class="mr-2">mdi-send</v-icon>
                        {{ isSendingQuestion ? 'Sending...' : 'Send answer' }}
                      </v-btn>

                      <v-btn
                        v-if="isRecordingQuestion"
                        class="cancel-recording-btn mt-2"
                        color="error"
                        outlined
                        block
                        @click="cancelRecording()"
                      >
                        <v-icon class="mr-2">mdi-cancel</v-icon>Cancel
                      </v-btn>
                      
                      <v-progress-circular
                        v-if="loadingAnswer"
                        indeterminate
                        color="primary"
                        size="24"
                        class="mt-2"
                      />
                    </div>

                    <v-textarea
                      v-if="isTranscripted"
                      v-model="answerTest"
                      :disabled="isSendingQuestion"
                      rows="3"
                      outlined
                      dense
                      class="mt-2 answer-textarea-compact"
                    />

                    <v-alert v-if="messageOptions" dense outlined color="error" class="mt-2 mb-0">
                      {{ messageOptions }}
                    </v-alert>
                    
                    <p class="question-counter mt-2 mb-0">
                      {{ stepsInterview.step.component.currentItem + 1 }}/{{ stepsInterview.step.component.itemsLength }}
                    </p>
                  </div>

                  <!-- QUESTION -->
                  <div v-if="item.type == 'QUESTION' && stepsInterview && !finalStep" class="step-content">
                    <!-- Recording Controls -->
                    <div class="recording-controls-compact">
                      <v-btn
                        v-if="!isTranscripted"
                        :disabled="loadingAnswer || !lastAudio"
                        class="ok-modal recording-btn"
                        :class="{ recording: isRecordingQuestion }"
                        block
                        @click="toggleRecording"
                      >
                        <v-icon v-if="isRecordingQuestion" class="mr-2">mdi-record-circle-outline</v-icon>
                        <v-icon v-else class="mr-2">mdi-record-rec</v-icon>
                        {{ isRecordingQuestion ? 'Recording...' : 'Start recording' }}
                      </v-btn>
                      <v-btn
                        v-else
                        :disabled="isSendingQuestion || !lastAudio || answerTest.toString().trim() === ''"
                        class="ok-modal recording-btn"
                        block
                        @click="toggleRecording"
                      >
                        <v-icon class="mr-2">mdi-send</v-icon>
                        {{ isSendingQuestion ? 'Sending...' : 'Send answer' }}
                      </v-btn>

                      <v-btn
                        v-if="isRecordingQuestion"
                        class="cancel-recording-btn mt-2"
                        color="error"
                        outlined
                        block
                        @click="cancelRecording()"
                      >
                        <v-icon class="mr-2">mdi-cancel</v-icon>Cancel
                      </v-btn>
                      
                      <v-progress-circular
                        v-if="loadingAnswer"
                        indeterminate
                        color="primary"
                        size="24"
                        class="mt-2"
                      />
                    </div>

                    <v-textarea
                      v-if="isTranscripted"
                      v-model="answerTest"
                      :disabled="isSendingQuestion"
                      rows="3"
                      outlined
                      dense
                      class="mt-2 answer-textarea-compact"
                    />

                    <v-alert v-if="messageOptions" dense outlined color="error" class="mt-2 mb-0">
                      {{ messageOptions }}
                    </v-alert>
                  </div>

                  <!-- LIST -->
                  <div v-if="item.type == 'LIST' && stepsInterview && !finalStep" class="step-content">
                    <div class="list-container">
                      <draggable v-model="itemsList">
                        <div
                          v-for="(i, id) in itemsList"
                          :key="id"
                          :class="isGrabbing ? 'cursor-grabbing' : 'cursor-grab'"
                          @mousedown="isGrabbing = true"
                          @mouseup="isGrabbing = false"
                          @mouseleave="isGrabbing = false"
                        >
                          <v-card class="list-item-card my-1 pa-2">
                            <span class="list-item-text">{{ i.content }}</span>
                          </v-card>
                        </div>
                      </draggable>
                    </div>
                    <v-btn
                      v-if="actualTypeStep !== 'TEST' && actualTypeStep !== 'QUESTION' && actualTypeStep !== 'INTRODUCTION' && !finalStep"
                      color="primary"
                      block
                      class="mt-3"
                      :disabled="!lastAudio"
                      @click="nextStep()"
                    >
                      Continue
                    </v-btn>
                  </div>

                  <!-- LIKERT -->
                  <div v-if="item.type == 'LIKERT' && stepsInterview && !finalStep" class="step-content">
                    <div class="likert-options">
                      <v-radio-group v-model="likertResponse" class="mt-1">
                        <v-radio
                          v-for="option in likertOptions"
                          :key="option"
                          :label="option"
                          :value="option"
                          class="likert-option"
                        />
                      </v-radio-group>
                    </div>
                    <div class="d-flex justify-space-between align-center mt-2">
                      <v-btn
                        v-if="!finalStep"
                        color="primary"
                        :disabled="!lastAudio || !likertResponse"
                        @click="sendLikertAnswer()"
                      >
                        Next
                      </v-btn>
                      <span class="question-counter">
                        {{ stepsInterview.step.component.currentItem + 1 }}/{{ stepsInterview.step.component.itemsLength }}
                      </span>
                    </div>
                  </div>

                  <!-- FINAL STEP - Feedback -->
                  <div v-if="finalStep" class="step-content feedback-content">
                    <div class="feedback-icons">
                      <v-icon size="50" :color="colors.bad" @click="opinion = 'bad'">mdi-emoticon-sad</v-icon>
                      <v-icon size="50" :color="colors.normal" @click="opinion = 'normal'">mdi-emoticon-neutral</v-icon>
                      <v-icon size="50" :color="colors.good" @click="opinion = 'good'">mdi-emoticon</v-icon>
                    </div>
                    <v-textarea
                      v-model="comment"
                      label="Comment (Optional)"
                      rows="2"
                      outlined
                      dense
                      class="mt-3"
                    />
                    <v-alert v-if="!successfullSendFeedback" dense outlined color="error" class="mt-2">
                      Error sending feedback. Please try again.
                    </v-alert>
                    <v-btn
                      class="ok-modal mt-2"
                      block
                      :disabled="opinion == null || !lastAudio"
                      @click="closeCall"
                    >
                      Finish interview
                    </v-btn>
                  </div>
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
          </div>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import AvatarInterviewService from '@/services/AvatarInterviewService.js';
import InterviewsDataService from '@/services/InterviewsDataService';
import SocketioService from '@/services/socketioService.js';
import UserMediaController from '@/services/UserMedia.js';
import VideosDataService from '@/services/VideosDataService.js';
import utils from '@/utils/utils';

export default {
  name: 'AvatarInterview',
  components: {
    draggable,
  },
  beforeRouteLeave(to, from, next) {
    if (this.selfUserStream) {
      UserMediaController.stopStream(this.selfUserStream);
    }

    this.selfUserStream = null;

    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
      this.lastAudio = false;
      this.audioQueue = [];
      this.receivedAudios = [];
      this.reproducingAudio = false;
      this.firstAudioPlaying = true;
    }
    SocketioService.disconnect(this.$socket);

    next();
  },
  data() {
    return {
      loadingUnity: true,
      unityInstance: null,
      inputAnimation: '',
      inputbundleUrlBackground: '',
      inputbundleUrlCharacter: '',
      inputBackground: '',
      inputCharacter: '',
      inputLookToCam: false,
      actualStep: 0,
      stepIndex: 0,
      actualTypeStep: '',

      receivedAudios: [],
      audioQueue: [],
      firsStepAudio: true,
      lastAudio: false,
      reproducingAudio: false,

      dynamicSteps: [],
      countSteps: 0,
      stepsInterview: null,
      testSuccess: false,
      sendAnswerFlag: false,
      finalAnswerTestSuccess: false,
      finalQuestionSuccess: false,
      answerQuestionSend: false,
      loadingAnswer: false,
      finalStep: false,
      firstAudioPlaying: true,
      audioId: -1,
      video: '',
      endUrl: '',
      question: '',
      answerTest: '',
      itemsList: [],
      messageOptions: '',

      conversationId: undefined,
      contextsSent: false,
      selfUserStream: null,
      candidateId: null,
      interview: {},
      cameraActivated: true,
      microActivated: true,
      redirectParams: {},
      selectedCamera: null,
      selectedMicro: null,
      selectedSpeaker: null,
      interviewType: null,
      interviewId: null,
      positionId: null,
      interviewAnalysisTypeId: null,
      avatarId: null,
      candidateVideo: null,
      recorder: {
        candidateVideo: null,
        candidateAudio: null,
      },

      colors: {
        good: 'green',
        normal: 'yellow',
        bad: 'red',
      },
      successfullSendFeedback: true,
      opinion: null,
      comment: '',
      selectedGood: false,
      selectedNormal: false,
      selectedBad: false,

      isGrabbing: false,
      lastMessage: '',

      isRecordingQuestion: false,
      isSendingQuestion: false,
      isTranscripted: false,
      mediaRecorderQuestion: null,
      audioChunksQuestion: [],
      streamQuestion: null,
      audioUrl: null,
      lastAudioStep: false,
      currentAudio: null,
      isCanceledAudio: false,

      index: 0,
      retries: 1,

      likertOptions: null,
      likertResponse: null,

      avatarMessage: '',
      audioLength: 0,
      timeOut: 0,

      isEndUrl: true,

      transcriptions: [],
      recordingStartTime: null, // Timestamp when video recording started

      // Control flag to prevent automatic step advancement without user response
      userHasAnswered: false,
      awaitingUserResponse: false,
    };
  },
  computed: {
    progressPercentage() {
      if (!this.dynamicSteps.length) return 0;
      const totalSteps = this.dynamicSteps.length;
      const currentStep = this.actualStep - 1 < 0 ? 0 : this.actualStep - 1;
      const percentage = Math.floor((currentStep / totalSteps) * 100);
      return Math.min(percentage, 100);
    },
    lastCompletedStepIndex() {
      let lastIndex = -1;
      for (let i = 0; i < this.dynamicSteps.length; i++) {
        if (this.actualStep > this.dynamicSteps[i].order + 1) {
          lastIndex = i;
        }
      }
      return lastIndex;
    },
    stepsToShow() {
      const steps = this.dynamicSteps;
      const current = this.actualStep;
      const lastIndex = steps.length - 1;

      const createStep = (idx, complete) => ({
        ...steps[idx],
        step: steps[idx].order + 1,
        complete,
        type: 'step',
      });

      if (steps.length <= 3) {
        return steps.map((item, i) => createStep(i, current > item.order + 1));
      }

      if (current === 1) {
        return [
          createStep(0, false),
          createStep(1, current > 2),
          { type: 'ellipsis' },
          createStep(lastIndex, current > lastIndex + 1),
        ];
      } else if (current > 1 && current < lastIndex) {
        return [
          createStep(current - 2, true),
          createStep(current - 1, false),
          { type: 'ellipsis' },
          createStep(lastIndex, current > lastIndex + 1),
        ];
      } else if (current === lastIndex) {
        return [
          createStep(current - 2, true),
          createStep(current - 1, false),
          createStep(lastIndex, false),
        ];
      } else if (current === lastIndex + 1) {
        return [
          createStep(current - 3, true),
          createStep(current - 2, true),
          createStep(current - 1, false),
        ];
      }

      return [];
    },
  },
  watch: {
    firstAudioPlaying() {
      // Only trigger getStepsInterview ONCE after initial presentation
      // stepIndex 0 = initial presentation, should load first real step
      // For other steps, wait for user response before advancing
      if (!this.firstAudioPlaying && this.stepIndex === 0) {
        this.getStepsInterview(this.stepIndex, 0);
      }
    },
    opinion() {
      if (this.opinion === 'good') {
        this.selectedGood = true;
        this.selectedNormal = false;
        this.selectedBad = false;
        this.colors.good = '#388E3C';
        this.colors.normal = '#FFF59D';
        this.colors.bad = '#EF9A9A';
      } else if (this.opinion === 'normal') {
        this.selectedGood = false;
        this.selectedNormal = true;
        this.selectedBad = false;
        this.colors.good = '#A5D6A7';
        this.colors.normal = '#FBC02D';
        this.colors.bad = '#EF9A9A';
      } else if (this.opinion === 'bad') {
        this.selectedGood = false;
        this.selectedNormal = false;
        this.selectedBad = true;
        this.colors.good = '#A5D6A7';
        this.colors.normal = '#FFF59D';
        this.colors.bad = '#D32F2F';
      }
    },
    lastAudioStep() {
      if (this.lastAudioStep) {
        console.log('[WATCHER] lastAudioStep triggered:', {
          answerQuestionSend: this.answerQuestionSend,
          userHasAnswered: this.userHasAnswered,
          currentItem: this.stepsInterview?.step?.component?.currentItem,
          itemsLength: this.stepsInterview?.step?.component?.itemsLength,
          stepType: this.actualTypeStep,
        });

        // CRITICAL FIX: Only advance if user has actually answered
        // This watcher was causing automatic progression without user response
        if (this.answerQuestionSend && this.userHasAnswered) {
          console.log('[WATCHER] User answered question - allowing nextStep');
          this.finalQuestionSuccess = true;
          this.answerQuestionSend = false;

          this.nextStep();
          return;
        }

        // CRITICAL FIX: Don't automatically advance on last item
        // The validation response handler will call nextStep() after user responds
        if (
          this.stepsInterview !== null &&
          this.stepsInterview.step !== undefined &&
          this.stepsInterview.step.component.currentItem + 1 ===
            this.stepsInterview.step.component.itemsLength &&
          this.userHasAnswered
        ) {
          console.log('[WATCHER] Last item and user answered - allowing nextStep');
          this.finalAnswerTestSuccess = true;
          this.nextStep();
          return;
        }

        console.log('[WATCHER] Avatar finished speaking - waiting for user response');
        this.testSuccess = false;
        this.sendAnswerFlag = false;
        this.messageOptions = '';
      }
    },
  },
  created() {
    SocketioService.emisor.$on('interviewValidateStepResponse', async (data) => {
      console.log('[VALIDATION] Received validation response:', {
        success: data.success,
        understood: data.understood,
        userHasAnswered: this.userHasAnswered,
        stepType: this.actualTypeStep,
      });

      // Only process if user actually sent an answer
      if (!this.userHasAnswered) {
        console.warn('[VALIDATION] Ignoring validation response - user has not answered yet');
        return;
      }

      if (data.understood) this.retries += 1;
      if (data.success) {
        this.lastAudioStep = false;
        this.retries += 1000;
        this.loadingAnswer = false;
        this.answerTest = '';
        this.isSendingQuestion = false;
        this.isTranscripted = false;

        // Reset the flag after successful processing
        this.userHasAnswered = false;
        this.awaitingUserResponse = false;

        if (this.actualTypeStep === 'LIKERT' || this.actualTypeStep === 'TEST') {
          if (
            this.stepsInterview.step.component.currentItem + 1 <
            this.stepsInterview.step.component.itemsLength
          ) {
            this.getStepsInterview(
              this.stepIndex,
              this.stepsInterview.step.component.currentItem + 1
            );
          } else {
            // TEST or LIKERT completed - set endTime for last transcription
            const elapsedTime = this.recordingStartTime 
              ? (Date.now() - this.recordingStartTime) / 1000 
              : null;
            const lastTranscription = this.transcriptions[this.transcriptions.length - 1];
            if (lastTranscription && elapsedTime !== null && lastTranscription.startTime !== null) {
              lastTranscription.endTime = elapsedTime;
            }
            this.nextStep();
          }
        } else if (this.actualTypeStep === 'QUESTION') {
          // QUESTION completed - ensure endTime is set before moving to next step
          const elapsedTime = this.recordingStartTime 
            ? (Date.now() - this.recordingStartTime) / 1000 
            : null;
          const lastTranscription = this.transcriptions[this.transcriptions.length - 1];
          if (lastTranscription && elapsedTime !== null && lastTranscription.startTime !== null) {
            // Only set endTime if not already set (it should be set in sendAnswerQuestion, but ensure it here too)
            if (lastTranscription.endTime === null) {
              lastTranscription.endTime = elapsedTime;
            }
          }
          this.nextStep();
        }
      } else {
        this.loadingAnswer = false;
        this.answerTest = '';
        this.isSendingQuestion = false;
        this.isTranscripted = false;
        this.sendAnswerFlag = false;
        // Reset flag on error as well
        this.userHasAnswered = false;
        if (data.understood) this.messageOptions = data.error;
      }
    });

    SocketioService.emisor.$on('dialogueAudioTranscriptionResponse', async (data) => {
      this.isTranscripted = false;
      if (data.success) {
        this.answerTest = data.transcription;
      } else {
        this.answerTest = '';
      }

      this.isTranscripted = true;
      this.loadingAnswer = false;
    });

    SocketioService.emisor.$on('authenticateResponse', async (data) => {
      if (!data.success) {
        this.isError = true;
        this.errorMessage =
          'Error authenticating the user connection. Please, contact an administrator.';
      } else {
        this.isError = false;
        this.errorMessage = '';
      }
    });

    SocketioService.emisor.$on('dialogueConversationResponse', async (data) => {
      console.log('[SOCKET] Conversation response received:', {
        success: !data.error,
        conversationId: data.conversationId,
        error: data.error,
      });

      const hasError =
        data.error &&
        ((typeof data.error === 'object' && Object.keys(data.error).length > 0) ||
          typeof data.error === 'string');

      if (!hasError) {
        // FIXED: Removed this.index += 1 to prevent automatic step skipping
        // The conversation response is only for confirming avatar connection is ready
        // Steps should only advance when candidate completes questions correctly

        this.loadingUnity = false;
        this.isError = false;
        this.errorMessage = '';

        // Only start recording if not already recording (prevent multiple starts)
        if (!this.recorder.candidateVideo || !this.recorder.candidateAudio) {
          UserMediaController.getUserMedia(
            async function (stream) {
              if (!stream) {
                stream = UserMediaController.getPlaceholderStream();
              }

              const videoStream = await navigator.mediaDevices.getUserMedia({
                video: this.selectedCamera ? { deviceId: { ideal: this.selectedCamera } } : true,
                audio: this.selectedMicro ? { deviceId: { ideal: this.selectedMicro } } : true,
              });

              UserMediaController.setMediaStreamVideoEnabled(videoStream, this.cameraActivated);
              UserMediaController.setMediaStreamAudioEnabled(videoStream, this.microActivated);

              this.selfUserStream = videoStream;

              this.candidateVideo = document.getElementById('candidateVideo');
              this.candidateVideo.srcObject = videoStream;

              this.recorder.candidateVideo = RecordRTC(this.selfUserStream, {
                type: 'video',
                mimeType: 'video/webm;codecs=vp8',
                disableLogs: true,
                timeSlice: 1000, // Better seeking support
              });

              this.recorder.candidateAudio = RecordRTC(this.selfUserStream, {
                type: 'audio',
                disableLogs: true,
              });

              this.recorder.candidateAudio.startRecording();
              this.recorder.candidateAudio.camera = this.selfUserStream;

              this.recorder.candidateVideo.startRecording();
              this.recorder.candidateVideo.camera = this.selfUserStream;
              
              // Capture recording start time for timestamp calculation
              this.recordingStartTime = Date.now();
              console.log('[RECORDING] Recording started at:', this.recordingStartTime);
            }.bind(this)
          );
        }

        this.conversationId = data.conversationId;

        // Send contexts only once when conversation is first established
        if (!this.contextsSent) {
          console.log('[SOCKET] Sending context:', {
            positionId: this.positionId,
            interviewAnalysisTypeId: this.interviewAnalysisTypeId,
          });
          SocketioService.sendContext(this.$socket, this.positionId, this.interviewAnalysisTypeId);
          this.contextsSent = true;
        }
      } else {
        this.isError = true;
        this.loadingUnity = false;

        let errorMsg = 'Error creating the conversation.';
        if (data.error) {
          if (typeof data.error === 'string') {
            errorMsg = data.error;
          } else if (data.error.message) {
            errorMsg = data.error.message;
          } else if (data.error.error) {
            errorMsg = data.error.error;
          }
        }

        this.errorMessage = `${errorMsg} Please, contact an administrator or try again.`;
        console.error('[ERROR] Failed to create conversation:', data.error);
      }
    });

    SocketioService.emisor.$on('dialogContextsResponse', async (data) => {
      console.log('[SOCKET] Context response received:', data);
    });

    SocketioService.emisor.$on('dialogueAudioMsgResponse', (data) => {
      console.log('[AUDIO] Audio chunk received:', {
        id: data.id,
        dataSize: data.data ? data.data.byteLength : 0,
        queueLength: this.audioQueue.length + 1,
      });
      this.receivedAudios.push({
        id: data.id,
        audioData: data.data,
        isReproduced: false,
      });
      this.audioQueue.push({ id: data.id, audioData: data.data });
      this.processAudioQueue();
    });

    SocketioService.emisor.$on('dialogueAudioMsgResponseEnd', (data) => {
      console.log('[AUDIO] Audio stream ended:', {
        id: data.id,
        firstAudioPlaying: this.firstAudioPlaying,
      });
      if (this.firstAudioPlaying) this.audioId = data.id || -1;
    });

    SocketioService.emisor.$on('dialogCompleteMsgResponse', async (data) => {
      console.log('[MESSAGE] Complete message received:', {
        message: data.data,
        messageLength: data.data ? data.data.length : 0,
      });
      this.lastMessage = data.data;
      this.audioLength = 0;

      if (!this.finalStep) {
        // Calculate elapsed time since recording started (in seconds)
        const elapsedTime = this.recordingStartTime 
          ? (Date.now() - this.recordingStartTime) / 1000 
          : 0;
        
        if (this.actualTypeStep === 'INTRODUCTION') {
          this.transcriptions.push({
            avatarMessage: this.lastMessage,
            order: this.stepIndex,
            typeStep: this.actualTypeStep,
            answer: null,
            interviewStepId: null,
            analysisRole: null,
            startTime: elapsedTime,
            endTime: null, // Will be set when answer is completed
          });
        } else if (this.actualTypeStep === 'VIDEO' || this.actualTypeStep === 'IFRAME') {
          this.transcriptions.push({
            avatarMessage: this.lastMessage,
            order: this.stepIndex,
            typeStep: this.actualTypeStep,
            answer: null,
            interviewStepId: this.stepsInterview.step.id,
            analysisRole: this.stepsInterview?.step?.analysisRole ?? null,
            startTime: elapsedTime,
            endTime: null, // Will be set when answer is completed
          });
        } else if (this.actualTypeStep === 'QUESTION') {
          this.transcriptions.push({
            avatarMessage: this.lastMessage,
            order: this.stepIndex,
            typeStep: this.actualTypeStep,
            answer: null,
            interviewStepId: this.stepsInterview.step.id,
            analysisRole: this.stepsInterview?.step?.analysisRole ?? null,
            startTime: elapsedTime,
            endTime: null, // Will be set when answer is completed
          });
        } else if (this.actualTypeStep === 'TEST') {
          this.transcriptions.push({
            avatarMessage: this.lastMessage,
            order: this.stepIndex,
            typeStep: this.actualTypeStep,
            answer: null,
            interviewStepId: this.stepsInterview.step.id,
            analysisRole: this.stepsInterview?.step?.analysisRole ?? null,
            startTime: elapsedTime,
            endTime: null, // Will be set when answer is completed
          });
        } else if (this.actualTypeStep === 'LIST') {
          this.transcriptions.push({
            avatarMessage: this.lastMessage,
            order: this.stepIndex,
            typeStep: this.actualTypeStep,
            answer: JSON.stringify(this.itemsList),
            interviewStepId: this.stepsInterview.step.id,
            analysisRole: this.stepsInterview?.step?.analysisRole ?? null,
            startTime: elapsedTime,
            endTime: null, // Will be set when answer is completed
          });
        } else if (this.actualTypeStep === 'LIKERT') {
          this.transcriptions.push({
            avatarMessage: this.lastMessage,
            order: this.stepIndex,
            typeStep: this.actualTypeStep,
            answer: this.likertResponse,
            interviewStepId: this.stepsInterview.step.id,
            analysisRole: this.stepsInterview?.step?.analysisRole ?? null,
            startTime: elapsedTime,
            endTime: null, // Will be set when answer is sent
          });
        }
      }
    });

    SocketioService.emisor.$on('dialoguePartialMsgResponse', async (data) => {
      console.log('[MESSAGE] Partial message received:', {
        partialText: data.data,
        currentLength: this.audioLength,
      });
      if (this.audioLength == 0) this.avatarMessage = '';
      else {
        this.avatarMessage = this.avatarMessage + data.data;

        this.scrollDown();
      }
      this.audioLength += 1;
    });

    SocketioService.emisor.$on('interviewLikertStepResponse', async (data) => {
      console.log('[LIKERT] Received Likert response:', {
        error: data.error,
        userHasAnswered: this.userHasAnswered,
      });

      // Only process if user actually sent an answer
      if (!this.userHasAnswered) {
        console.warn('[LIKERT] Ignoring Likert response - user has not answered yet');
        return;
      }

      if (data.error !== null) {
        this.isError = true;
        this.errorMessage = 'Error saving the Likert response. Please, contact an administrator.';
      }

      // Reset flag after processing
      this.userHasAnswered = false;
      this.likertResponse = null;

      this.getStepsInterview(this.stepIndex, this.stepsInterview.step.component.currentItem + 1);
    });

    AvatarInterviewService.getLikertOptions()
      .then((response) => {
        this.likertOptions = response.data.data.data;
        this.likertOptions.reverse();
      })
      .catch((error) => {
        console.error((error.response && error.response.data) || error.message || error.toString());
      });
  },
  async mounted() {
    if (this.$route.params.interview === undefined) {
      this.$router.push(`/waiting-room-candidate-avatar?${localStorage.getItem('query')}`);
    } else {
      this.candidateId = this.$route.params.candidateId;
      this.interview = this.$route.params.interview;
      this.cameraActivated = this.$route.params.video;
      this.microActivated = this.$route.params.audio;
      this.redirectParams = this.$route.params.redirectParams;
      this.selectedCamera = this.$route.params.camera;
      this.selectedMicro = this.$route.params.microphone;
      this.selectedSpeaker = this.$route.params.speaker;

      this.interviewType = this.interview.interviewType;
      this.interviewId = this.interview.id;
      this.positionId = this.interview.position.id;
      this.interviewAnalysisTypeId = this.interview.interviewAnalysisType;
      this.avatarId = this.interview.avatarId;

      console.log('[INIT] AvatarInterview mounted with data:', {
        candidateId: this.candidateId,
        interviewId: this.interviewId,
        interviewType: this.interviewType,
        positionId: this.positionId,
        avatarId: this.avatarId,
        hasSocket: !!this.$socket,
      });

      const query = new URLSearchParams({ ...this.redirectParams });
      localStorage.setItem('query', query.toString());

      //TODO: DETECTAR CAMBIO DE URL EN EL IFRAME CUANDO CORRESPONDA

      // En el contenido del iframe (en la página cargada dentro del iframe):
      // window.addEventListener("hashchange", function() {
      //   window.parent.postMessage(window.location.href, "*");
      // });

      // window.addEventListener("popstate", function() {
      //   window.parent.postMessage(window.location.href, "*");
      // });

      // En la página principal (donde está el iframe):
      // window.addEventListener("message", function(event) {
      //   if (event.origin !== "URL_DEL_IFRAME") {
      //     return;
      //   }

      //   const currentUrl = event.data;
      //   if (currentUrl.includes("url_especifica")) {
      //     console.log("Se llegó a la URL específica dentro del iframe");
      //   }
      // });

      await this.getDynamicSteps();
      await this.$nextTick();

      const tryLoadUnity = () => {
        const container = document.querySelector('#unity-container');
        const canvas = document.querySelector('#unity-canvas');
        const loadingBar = document.querySelector('#unity-loading-bar');
        const progressBarFull = document.querySelector('#unity-progress-bar-full');
        const fullscreenButton = document.querySelector('#unity-fullscreen-button');
        const warningBanner = document.querySelector('#unity-warning');

        if (!canvas || !container || !loadingBar || !progressBarFull || !warningBanner) {
          console.warn('Canvas o elementos de Unity aún no disponibles, reintentando...');
          setTimeout(tryLoadUnity, 100);
          return;
        }

        function unityShowBanner(msg, type) {
          function updateBannerVisibility() {
            warningBanner.style.display = warningBanner.children.length ? 'block' : 'none';
          }
          const div = document.createElement('div');
          div.innerHTML = msg;
          warningBanner.appendChild(div);
          if (type === 'error') {
            div.style.background = 'red';
            div.style.padding = '10px';
          } else if (type === 'warning') {
            div.style.background = 'yellow';
            div.style.padding = '10px';
            setTimeout(() => {
              warningBanner.removeChild(div);
              updateBannerVisibility();
            }, 5000);
          }
          updateBannerVisibility();
        }

        const buildUrl =
          utils.config.publicBucketURL +
          '/' +
          utils.avatar.folder +
          '/' +
          utils.avatar.version +
          '/';

        const loaderUrl = buildUrl + utils.avatar.loaderFile;
        const config = {
          dataUrl: buildUrl + utils.avatar.buildFile,
          frameworkUrl: buildUrl + utils.avatar.frameworkFile,
          codeUrl: buildUrl + utils.avatar.wasmFile,
          streamingAssetsUrl: 'StreamingAssets',
          companyName: 'DefaultCompany',
          productName: 'VirtualProfilerJS',
          productVersion: '0.1.0',
          showBanner: unityShowBanner,
        };

        if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
          const meta = document.createElement('meta');
          meta.name = 'viewport';
          meta.content =
            'width=device-width, height=device-height, initial-scale=1.0, user-scalable=no, shrink-to-fit=yes';
          document.head.appendChild(meta);
          container.className = 'unity-mobile';
          canvas.className = 'unity-mobile';
        }

        loadingBar.style.display = 'block';

        const script = document.createElement('script');
        script.src = loaderUrl;

        script.onload = () => {
          console.log('Script de Unity cargado correctamente.');
          if (!window.createUnityInstance) {
            console.error('createUnityInstance no está definido.');
            return;
          }

          window
            .createUnityInstance(canvas, config, (progress) => {
              progressBarFull.style.width = 100 * progress + '%';
            })
            .then((instance) => {
              this.unityInstance = instance;
              console.log('[UNITY] Unity instance created successfully');
              setTimeout(async () => {
                console.log('[SOCKET] Creating conversation with:', {
                  avatarId: this.avatarId,
                  conversationId: this.conversationId,
                });
                await SocketioService.createConversation(
                  this.$socket,
                  this.avatarId,
                  this.conversationId
                );
              }, 2000);
            })
            .catch((message) => {
              console.error('No se pudo crear la instancia de Unity:', message);
              alert(message);
            });
        };

        document.body.appendChild(script);
      };

      tryLoadUnity();
    }
  },
  beforeDestroy() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
      this.lastAudio = false;
      this.audioQueue = [];
      this.receivedAudios = [];
      this.reproducingAudio = false;
      this.firstAudioPlaying = true;
    }
  },
  methods: {
    scrollDown() {
      this.$nextTick(() => {
        const element = document.getElementById('callPlayerTextAvatar');
        element.scrollTop = element.scrollHeight;
      });
    },
    playSingleAudio(audioData) {
      return new Promise(async (resolve, reject) => {
        console.log('[AUDIO] Starting to play audio chunk:', {
          dataSize: audioData ? audioData.byteLength : 0,
        });
        const blob = new Blob([audioData], { type: 'audio/wav' });
        const url = URL.createObjectURL(blob);
        this.currentAudio = new Audio(url);
        this.currentAudio.onended = () => {
          console.log('[AUDIO] Audio chunk finished playing');
          URL.revokeObjectURL(url);
          resolve();
        };
        this.currentAudio.onerror = (error) => {
          console.error('[AUDIO] Error playing audio:', error);
          reject(error);
        };
        try {
          await this.currentAudio.play();
          console.log('[AUDIO] Audio.play() called successfully');
        } catch (error) {
          console.error('[AUDIO] Error calling Audio.play():', error);
          reject(error);
        }
      });
    },
    async processAudioQueue() {
      console.log('[QUEUE] Processing audio queue:', {
        queueLength: this.audioQueue.length,
        isReproducing: this.reproducingAudio,
        lastAudio: this.lastAudio,
        unityLoaded: !!this.unityInstance,
      });
      this.lastAudio = false;
      if (this.audioQueue.length === 0) {
        console.log('[AVATAR] Setting avatar to IDLE (queue empty)');
        if (this.unityInstance) {
          this.unityInstance.SendMessage('JavaScriptHook', 'PlayAnimation', 'Idle');
        }
        this.lastAudio = true;
        if (!this.lastAudioStep) this.lastAudioStep = true;
      } else if (this.audioQueue.length > 0 && !this.reproducingAudio) {
        this.reproducingAudio = true;
        const data = this.audioQueue.shift();
        console.log('[AVATAR] Setting avatar to TALK, playing audio:', {
          audioId: data.id,
          remainingInQueue: this.audioQueue.length,
        });
        if (this.unityInstance) {
          this.unityInstance.SendMessage('JavaScriptHook', 'PlayAnimation', 'Talk');
        }
        await this.playSingleAudio(data.audioData);

        if (this.audioId === data.id) {
          console.log('[AUDIO] First audio finished playing');
          this.firstAudioPlaying = false;
        }

        this.reproducingAudio = false;
        console.log('[AVATAR] Setting avatar to IDLE (audio finished)');
        if (this.unityInstance) {
          this.unityInstance.SendMessage('JavaScriptHook', 'PlayAnimation', 'Idle');
        }
        this.processAudioQueue();
      }
    },
    getDynamicSteps() {
      AvatarInterviewService.getDynamicSteps(this.interviewType).then((response) => {
        if (response.status == 200) {
          this.dynamicSteps = response.data.data.steps;

          this.dynamicSteps.sort((a, b) => a.order - b.order);

          const firstStep = {
            name: 'Introduction',
            order: 0,
            type: 'INTRODUCTION',
          };

          this.dynamicSteps.unshift(firstStep);

          this.dynamicSteps = this.dynamicSteps.map((item, index) => {
            return { ...item, order: index };
          });

          this.actualStep = this.dynamicSteps[0].order + 1;
          this.stepIndex = this.dynamicSteps[0].order;
          this.actualTypeStep = this.dynamicSteps[0].type;
          this.countSteps = this.dynamicSteps.length;
        } else {
          console.error('error: ', response.data);
        }
      });
    },
    checkStep() {
      if (this.actualTypeStep === 'VIDEO') {
        this.getStepsInterviewVideo();
      } else if (this.actualTypeStep === 'TEST') {
        this.getStepsInterviewQuestion();
      } else if (this.actualTypeStep === 'LIST') {
        this.getStepsInterviewList();
      } else if (this.actualTypeStep === 'QUESTION') {
        this.finalQuestionSuccess = false;
        this.getStepsInterviewQuestion();
      } else if (this.actualTypeStep === 'LIKERT') {
        this.getStepsInterviewLikert();
      } else if (this.actualTypeStep === 'IFRAME') {
        this.getStepsInterviewIframe();
      }
    },
    async getStepsInterview(newStep, question) {
      console.log('[STEP] Getting interview step:', {
        stepIndex: newStep,
        questionNumber: question,
        interviewType: this.interviewType,
        interviewId: this.interviewId,
      });

      // Clean up flags from previous step to prevent state leakage
      this.userHasAnswered = false;
      this.answerQuestionSend = false;
      this.awaitingUserResponse = true;
      this.lastAudioStep = false;

      if (newStep !== 0) {
        newStep -= 1;
      }

      await AvatarInterviewService.getInterviewSteps(
        this.interviewType,
        this.interviewId,
        newStep,
        question
      )
        .then((response) => {
          if (response.status == 200) {
            this.stepsInterview = response.data.data;
            const { name, description, component } = this.stepsInterview.step;
            const newValue = {
              name,
              description,
              component,
              progress: this.progressPercentage,
            };
            console.log('[SOCKET] Sending interview step to speak:', {
              stepName: name,
              stepDescription: description?.substring(0, 50) + '...',
              hasComponent: !!component,
              progress: this.progressPercentage,
              awaitingUserResponse: this.awaitingUserResponse,
            });
            SocketioService.sendInterviewSpeakStep(this.$socket, newValue);
            this.lastAudio = false;
            if (newStep === 0) {
              this.actualTypeStep = this.dynamicSteps[this.actualStep].type;
              this.actualStep += 1;
              this.stepIndex += 1;
            }
            this.checkStep();
          } else {
            console.error('error: ', response.data);
          }
        })
        .catch((error) => {
          console.error(
            (error.response && error.response.data) || error.message || error.toString()
          );
        });
    },
    async getStepsInterviewVideo() {
      const rawUrl = this.stepsInterview.step.component.items[0].content;

      let videoId = '';

      if (rawUrl.includes('youtu.be')) {
        videoId = rawUrl.split('youtu.be/')[1].split('?')[0];
      } else if (rawUrl.includes('watch?v=')) {
        videoId = rawUrl.split('watch?v=')[1].split('&')[0];
      }

      this.video = videoId ? `https://www.youtube.com/embed/${videoId}` : '';
    },
    async getStepsInterviewIframe() {
      const rawUrl = this.stepsInterview.step.component.items[0].content;

      if (rawUrl.includes('|||')) {
        this.video = rawUrl.split('|||')[0];
        this.endUrl = rawUrl.split('|||')[1];
      } else {
        this.video = rawUrl;
        this.endUrl = null;
        this.isEndUrl = true;
      }
    },
    async getStepsInterviewQuestion() {
      this.question = this.stepsInterview.step.component.items[0].content;
    },
    async getStepsInterviewList() {
      this.itemsList = this.stepsInterview.step.component.items;
    },
    async getStepsInterviewLikert() {
      this.question = this.stepsInterview.step.component.items[0].content;
    },
    sendAnswerQuestion() {
      this.answerQuestionSend = true;
      this.sendAnswer();
    },
    sendAnswer() {
      console.log('[ANSWER] User is sending answer:', {
        stepType: this.actualTypeStep,
        answer: this.answerTest?.substring(0, 50) + '...',
        stepIndex: this.stepIndex,
      });

      this.messageOptions = '';

      // Mark that user has answered - this allows validation response to proceed
      this.userHasAnswered = true;

      if (this.actualTypeStep === 'TEST') {
        this.testSuccess = false;
        this.sendAnswerFlag = true;
      }

      const body = {
        question: {
          title: this.stepsInterview.step.name,
          content: this.stepsInterview.step.component.items[0].content,
        },
        answer: this.answerTest,
        componentStepId: this.stepsInterview.step.component.items[0].id,
        avatarSpeech: this.lastMessage,
        interviewId: this.interviewId,
        questionOrder:
          this.actualTypeStep === 'TEST' ? this.stepsInterview.step.component.currentItem : 0,
        interviewTypeStepId: this.stepsInterview.step.id,
        attempt: this.retries,
      };

      if (this.actualTypeStep === 'QUESTION') {
        // Calculate elapsed time when QUESTION answer is sent
        const elapsedTime = this.recordingStartTime 
          ? (Date.now() - this.recordingStartTime) / 1000 
          : null;
        const lastTranscription = this.transcriptions[this.transcriptions.length - 1];
        if (lastTranscription) {
          lastTranscription.answer = this.answerTest;
          if (elapsedTime !== null && lastTranscription.startTime !== null) {
            lastTranscription.endTime = elapsedTime;
          }
        }
      } else if (this.actualTypeStep === 'TEST') {
        if (this.transcriptions[this.transcriptions.length - 1].answer === null) {
          this.transcriptions[this.transcriptions.length - 1].answer = [];

          this.transcriptions[this.transcriptions.length - 1].answer.push({
            question: body.question,
            questionId: this.stepsInterview.step.component.items[0].id,
            answer: body.answer,
            order: body.questionOrder,
          });
        } else if (
          body.questionOrder ===
          this.transcriptions[this.transcriptions.length - 1].answer[
            this.transcriptions[this.transcriptions.length - 1].answer.length - 1
          ].order
        ) {
          this.transcriptions[this.transcriptions.length - 1].answer[
            this.transcriptions[this.transcriptions.length - 1].answer.length - 1
          ] = {
            question: body.question,
            questionId: this.stepsInterview.step.component.items[0].id,
            answer: body.answer,
            order: body.questionOrder,
          };
        } else if (
          body.questionOrder !==
          this.transcriptions[this.transcriptions.length - 1].answer[
            this.transcriptions[this.transcriptions.length - 1].answer.length - 1
          ].order
        ) {
          this.transcriptions[this.transcriptions.length - 1].answer.push({
            question: body.question,
            questionId: this.stepsInterview.step.component.items[0].id,
            answer: body.answer,
            order: body.questionOrder,
          });
        }
      }

      SocketioService.sendInterviewValidateStep(this.$socket, body);
    },
    sendLikertAnswer() {
      console.log('[ANSWER] User is sending Likert answer:', {
        response: this.likertResponse,
        stepIndex: this.stepIndex,
      });

      // Mark that user has answered
      this.userHasAnswered = true;

      // Calculate elapsed time when LIKERT answer is sent
      const elapsedTime = this.recordingStartTime 
        ? (Date.now() - this.recordingStartTime) / 1000 
        : null;
      const lastTranscription = this.transcriptions[this.transcriptions.length - 1];
      if (lastTranscription && elapsedTime !== null && lastTranscription.startTime !== null) {
        lastTranscription.endTime = elapsedTime;
      }

      const body = {
        componentStepId: this.stepsInterview.step.component.items[0].id,
        value: this.likertResponse,
        interviewId: this.interviewId,
      };

      SocketioService.sendInterviewLikertAnswer(this.$socket, body);
    },
    nextStep() {
      console.log('[NEXTSTEP] Advancing to next step:', {
        currentStep: this.stepIndex,
        currentType: this.actualTypeStep,
        userHasAnswered: this.userHasAnswered,
      });

      // Set endTime for current step before advancing (for steps without user input like INTRODUCTION, VIDEO, IFRAME)
      const elapsedTime = this.recordingStartTime 
        ? (Date.now() - this.recordingStartTime) / 1000 
        : null;
      const lastTranscription = this.transcriptions[this.transcriptions.length - 1];
      if (lastTranscription && elapsedTime !== null && lastTranscription.startTime !== null) {
        // Only set endTime if not already set (for steps with user input, endTime is set when answer is sent)
        if (lastTranscription.endTime === null && 
            (this.actualTypeStep === 'INTRODUCTION' || 
             this.actualTypeStep === 'VIDEO' || 
             this.actualTypeStep === 'IFRAME')) {
          lastTranscription.endTime = elapsedTime;
        }
      }

      let body;
      if (this.itemsList.length > 0) {
        this.itemsList = this.itemsList.map((item) => item.id);
        body = {
          interviewId: this.interviewId,
          candidateId: this.candidateId,
          items: this.itemsList,
        };

        // Calculate elapsed time when LIST answer is completed
        if (lastTranscription && elapsedTime !== null && lastTranscription.startTime !== null) {
          lastTranscription.answer = JSON.stringify(this.itemsList);
          if (lastTranscription.endTime === null) {
            lastTranscription.endTime = elapsedTime;
          }
        }
      } else {
        body = {
          interviewId: this.interviewId,
          candidateId: this.candidateId,
        };
      }

      AvatarInterviewService.completeStep(this.interviewType, this.stepIndex, body).then(
        (response) => {
          if (response.status == 200) {
            this.retries = 1;

            // Clean flags before advancing to next step
            this.userHasAnswered = false;
            this.answerQuestionSend = false;
            this.awaitingUserResponse = false;

            if (this.actualStep == this.dynamicSteps.length) {
              this.finalStep = true;
              SocketioService.sendFinalMessage(this.$socket, this.interviewId, this.positionId);
              return;
            }
            this.actualTypeStep = this.dynamicSteps[this.actualStep].type;
            this.actualStep += 1;
            this.stepIndex += 1;

            if (this.itemsList.length > 0) {
              this.itemsList = [];
            }

            console.log('[NEXTSTEP] Calling getStepsInterview for step:', this.stepIndex);
            this.getStepsInterview(this.stepIndex, 0);
          } else {
            console.error('error: ', response.data);
          }
        }
      );
    },
    async stopRecording() {
      if (this.selfUserStream) {
        UserMediaController.stopStream(this.selfUserStream);
        this.selfUserStream = null;
      }

      const promises = [];

      if (this.recorder.candidateAudio !== null) {
        promises.push(
          new Promise((resolve, reject) => {
            this.recorder.candidateAudio.stopRecording(() => {
              try {
                const candidateAudioFile = this.recorder.candidateAudio.getBlob();

                this.recorder.candidateAudio.camera.stop();
                this.recorder.candidateAudio.destroy();
                this.recorder.candidateAudio = null;

                return resolve({
                  blob: candidateAudioFile,
                  type: 'audio',
                  source: 'candidate',
                });
              } catch (error) {
                return reject(error);
              }
            });
          })
        );
      }

      if (this.recorder.candidateVideo !== null) {
        promises.push(
          new Promise((resolve, reject) => {
            this.recorder.candidateVideo.stopRecording(() => {
              try {
                const candidateVideoFile = this.recorder.candidateVideo.getBlob();

                this.recorder.candidateVideo.camera.stop();
                this.recorder.candidateVideo.destroy();
                this.recorder.candidateVideo = null;

                return resolve({
                  blob: candidateVideoFile,
                  type: 'video',
                  source: 'candidate',
                });
              } catch (error) {
                return reject(error);
              }
            });
          })
        );
      }

      try {
        const recordings = await Promise.all(promises);

        await InterviewsDataService.updateInterviewTranscription(this.interviewId, {
          transcription: JSON.stringify(this.transcriptions),
        });

        const uploadRes = await VideosDataService.uploadVideoAvatar(
          recordings,
          this.interviewId,
          this.candidateId
        );

        console.log('[VIDEO] Video upload completed:', {
          status: uploadRes.status,
          interviewId: this.interviewId,
          candidateId: this.candidateId,
        });

        if (uploadRes.status == 200 || uploadRes.status == 202) {
          if (
            !this.interview.urlRedirect.startsWith('http://') &&
            !this.interview.urlRedirect.startsWith('https://')
          )
            window.location.replace('https://' + this.interview.urlRedirect);
          else window.location.replace(this.interview.urlRedirect);
        } else {
          console.error('[VIDEO] Error uploading video - unexpected status:', uploadRes.status);
        }
      } catch (err) {
        console.error('[RECORDING] Error in stopRecording:', err);
      }
    },

    async startRecordingQuestion() {
      try {
        this.isCanceledAudio = false;
        this.audioUrl = null;

        this.streamQuestion = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        this.mediaRecorderQuestion = new MediaRecorder(this.streamQuestion);
        this.audioChunksQuestion = [];

        this.mediaRecorderQuestion.ondataavailable = (event) => {
          this.audioChunksQuestion.push(event.data);
        };

        this.mediaRecorderQuestion.onstop = async () => {
          if (!this.isCanceledAudio) {
            const audioBlob = new Blob(this.audioChunksQuestion, {
              type: 'audio/webm',
            });
            this.audioUrl = audioBlob;

            if (!this.audioUrl) {
              this.isSendingQuestion = false;
              this.loadingAnswer = false;
              this.messageOptions = 'Error sending question.';
              return;
            }
            this.messageOptions = '';
            const reader = new FileReader();
            reader.onloadend = () => {
              const res = reader.result;
              SocketioService.sendAudioQuestion(this.$socket, res);
            };
            reader.readAsArrayBuffer(this.audioUrl);
          }
        };

        this.mediaRecorderQuestion.start();
        this.isRecordingQuestion = true;
      } catch (error) {
        console.error('Error accessing the microphone :', error);
      }
    },
    stopRecordingQuestion() {
      this.isCanceledAudio = false;
      this.loadingAnswer = true;
      if (this.mediaRecorderQuestion) {
        this.mediaRecorderQuestion.stop();
        this.isRecordingQuestion = false;

        const tracks = this.streamQuestion.getTracks();
        tracks.forEach((track) => track.stop());

        this.streamQuestion = null;
      }
    },
    toggleRecording() {
      if (this.isRecordingQuestion) {
        this.stopRecordingQuestion();
      } else if (this.isTranscripted) {
        this.isSendingQuestion = true;
        this.loadingAnswer = true;
        if (this.actualTypeStep === 'QUESTION') this.sendAnswerQuestion();
        else if (this.actualTypeStep === 'TEST') this.sendAnswer();
      } else {
        this.startRecordingQuestion();
      }
    },
    cancelRecording() {
      this.isCanceledAudio = true;
      if (this.mediaRecorderQuestion) {
        this.mediaRecorderQuestion.stop();
        this.isRecordingQuestion = false;
        this.isSendingQuestion = false;
        this.loadingAnswer = false;

        if (this.streamQuestion) {
          const tracks = this.streamQuestion.getTracks();
          tracks.forEach((track) => track.stop());
          this.streamQuestion = null;
        }

        this.audioChunksQuestion = [];
        this.audioUrl = null;
      }

      this.mediaRecorderQuestion = null;
    },

    closeCall() {
      this.successfullSendFeedback = true;
      AvatarInterviewService.sendFeedback(
        this.opinion,
        this.comment,
        this.candidateId,
        this.interviewId,
        this.interviewType
      ).then(
        (response) => {
          if (response.status == 200 || response.status == 201) {
            this.stopRecording();
          } else {
            this.successfullSendFeedback = false;
          }
        },
        (error) => {
          if (error.response.status === 401 || error.response.status === 403) {
            localStorage.removeItem('user');
            this.$router.push('/');
          } else {
            this.successfullSendFeedback = false;
          }

          console.error(
            (error.response && error.response.data) || error.message || error.toString()
          );
        }
      );
    },
  },
};
</script>
